'use client';

import React, { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useWillStore } from '@/lib/store';
import { useAutoSave } from '@/lib/hooks/useAutoSave';
import { StepIndicator } from './StepIndicator';
import { PersonalDetails } from './steps/PersonalDetails';
import { Executors } from './steps/Executors';
import { ChildrenGuardians } from './steps/ChildrenGuardians';
import { Beneficiaries } from './steps/Beneficiaries';
import { Optionals } from './steps/Optionals';
import { ReviewPayment } from './steps/ReviewPayment';
import { Skeleton } from '@/app/components/ui/Skeleton';

export const MultiStepForm = () => {
  const currentStep = useWillStore((state: { currentStep: number }) => state.currentStep);
  const isInPaymentMode = useWillStore((state) => state.isInPaymentMode);
  const [isHydrated, setIsHydrated] = React.useState(() => {
    const persistApi = useWillStore.persist;
    return persistApi?.hasHydrated?.() ?? true;
  });

  // Enable auto-save to Supabase
  useAutoSave();

  useEffect(() => {
    // Tie hydration to Zustand persist (avoids timing hacks + blank flashes).
    // Access persist API directly inside effect to avoid dependency array issues.
    const persistApi = useWillStore.persist;
    const unsub = persistApi?.onFinishHydration?.(() => setIsHydrated(true));
    return () => unsub?.();
  }, []);

  if (!isHydrated) {
    return (
      <div className="max-w-6xl mx-auto w-full px-4 py-10" role="status" aria-live="polite">
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="mt-6 h-32 w-full rounded" />
      </div>
    );
  }

  const STEPS = [
    { id: 0, component: PersonalDetails },
    { id: 1, component: ChildrenGuardians },
    { id: 2, component: Beneficiaries },
    { id: 3, component: Executors },
    { id: 4, component: Optionals },
    { id: 5, component: ReviewPayment },
  ];

  const renderStep = () => {
    const StepComponent = STEPS.find(s => s.id === currentStep)?.component || PersonalDetails;
    return <StepComponent />;
  };

  const containerClasses = cn(
    "w-full mx-auto pb-16 px-4 md:px-6",
    isInPaymentMode ? "max-w-7xl md:px-10" : "max-w-5xl"
  );

  return (
    <div className={containerClasses}>
      <div className="animate-fade-in-up mx-auto w-full">
        <div className={isInPaymentMode ? "" : "mt-2 md:mt-4"}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

